import { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './PhotoViewer.module.css';

interface Photo {
  id: string;
  url: string;
  caption?: string;
}

interface PhotoViewerProps {
  photos: Photo[];
  initialIndex: number;
  visible: boolean;
  onClose: () => void;
}

export function PhotoViewer({ photos, initialIndex, visible, onClose }: PhotoViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, visible]);

  // Reset loaded state when modal closes
  useEffect(() => {
    if (!visible) {
      setLoadedImages(new Set());
    }
  }, [visible]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (visible) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [visible]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!visible) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, currentIndex]);

  if (!visible || photos.length === 0) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swiped left → next
        handleNext();
      } else {
        // Swiped right → prev
        handlePrev();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const currentPhoto = photos[currentIndex];
  const isCurrentLoaded = loadedImages.has(currentPhoto.id);

  const handleImageLoad = () => {
    setLoadedImages(prev => new Set(prev).add(currentPhoto.id));
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div 
        className={styles.container} 
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          <X size={24} />
        </button>

        <div className={`${styles.imageContainer} ${isCurrentLoaded ? styles.loaded : ''}`}>
          <img
            key={currentPhoto.id}
            src={currentPhoto.url}
            alt={currentPhoto.caption || 'Photo'}
            className={`${styles.image} ${isCurrentLoaded ? styles.imageLoaded : ''}`}
            draggable={false}
            onLoad={handleImageLoad}
          />
        </div>

        {photos.length > 1 && (
          <>
            <button 
              className={`${styles.navButton} ${styles.prevButton}`} 
              onClick={handlePrev}
              aria-label="Previous photo"
            >
              <ChevronLeft size={28} />
            </button>
            <button 
              className={`${styles.navButton} ${styles.nextButton}`} 
              onClick={handleNext}
              aria-label="Next photo"
            >
              <ChevronRight size={28} />
            </button>
          </>
        )}

        <div className={styles.footer}>
          {currentPhoto.caption && (
            <p className={styles.caption}>{currentPhoto.caption}</p>
          )}
          {photos.length > 1 && (
            <>
              <p className={styles.counter}>
                {currentIndex + 1} / {photos.length}
              </p>
              <p className={styles.swipeHint}>Swipe, tap arrows, or use ← → keys</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

